import { AccountTree, Business, MyLocation } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  Grid2,
  Paper,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { getManagerCounters, getRecentEntities } from "../../api/app";
import { useTranslationHook } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { Entity } from "../../types/model/entity/Entity";
import { ManagerCounters } from "../../types/model/stats";
import EntitiesTable from "../Entities/EntitiesTable";
import StatCard from "./StatCard";

const ManagerDashboard = () => {
  const { t } = useTranslationHook();
  const handleError = useErrorHandler();

  const [counters, setCounters] = useState<ManagerCounters>();
  const [countersLoading, setCountersLoading] = useState(false);

  const [userEntities, setUserEntities] = useState<Entity[]>([]);
  const [userEntitiesLoading, setUserEntitiesLoading] = useState(false);

  const [globalEntities, setGlobalEntities] = useState<Entity[]>([]);
  const [globalEntitiesLoading, setGlobalEntitiesLoading] = useState(false);

  const fetchCounters = async () => {
    setCountersLoading(true);
    try {
      const data = await getManagerCounters();
      setCounters(data);
    } catch (error) {
      handleError(error);
    } finally {
      setCountersLoading(false);
    }
  };

  const fetchRecentEntities = async () => {
    setUserEntitiesLoading(true);
    setGlobalEntitiesLoading(true);
    try {
      const data = await getRecentEntities();
      setUserEntities(data.userRecent);
      setGlobalEntities(data.globalRecent);
    } catch (error) {
      handleError(error);
    } finally {
      setUserEntitiesLoading(false);
      setGlobalEntitiesLoading(false);
    }
  };

  useEffect(() => {
    fetchCounters();
    fetchRecentEntities();
  }, []);

  const handleRequestRefreshEntities = () => {
    fetchRecentEntities();
  };

  let countersContent;
  if (countersLoading) {
    countersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (counters) {
    countersContent = (
      <Grid2 container spacing={2} columns={12} sx={{ mb: (theme) => theme.spacing(4) }}>
        <Grid2 key="counters-entities" size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            icon={<Business />}
            title={t("generic.stats.entities")}
            total={counters.entities.total}
            active={counters.entities.active}
          />
        </Grid2>
        <Grid2 key="counters-managementUnits" size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            icon={<AccountTree />}
            title={t("generic.stats.management-units")}
            total={counters.managementUnits.total}
            active={counters.managementUnits.active}
          />
        </Grid2>
        <Grid2 key="counters-captures" size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            icon={<MyLocation />}
            title={t("generic.stats.captures")}
            total={counters.captures.total}
            active={counters.captures.active}
          />
        </Grid2>
      </Grid2>
    );
  } else {
    countersContent = (
      <Typography align="center" sx={{ mt: 2 }}>
        {t("generic.stats.no-data")}
      </Typography>
    );
  }

  let userEntitiesContent;
  if (userEntitiesLoading) {
    userEntitiesContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (userEntities.length > 0) {
    userEntitiesContent = (
      <>
        <Typography variant="h4" sx={{ mb: 3, color: "text.secondary" }}>
          {t("generic.stats.user-recent-entities")}
        </Typography>
        <EntitiesTable
          tableData={userEntities}
          onRequestRefresh={handleRequestRefreshEntities}
          showAddButton={false}
          disablePagination
        />
      </>
    );
  }

  let globalEntitiesContent;
  if (globalEntitiesLoading) {
    globalEntitiesContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (globalEntities.length > 0) {
    globalEntitiesContent = (
      <>
        <Typography variant="h4" sx={{ mb: 3, color: "text.secondary" }}>
          {t("generic.stats.global-recent-entities")}
        </Typography>
        <EntitiesTable
          tableData={globalEntities}
          onRequestRefresh={handleRequestRefreshEntities}
          showAddButton={false}
          disablePagination
        />
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Counters */}
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: 2
        }}
      >
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t("generic.stats.overview")}
        </Typography>
        {countersContent}

        <Divider sx={{ my: 1 }} />

        {/* User's Recently Created and Modified Entities Table */}
        {userEntitiesContent}

        {/* Global Recently Created and Modified Entities Table */}
        {globalEntitiesContent}
      </Paper>
    </Box>
  );
}

export default ManagerDashboard;